import { Alert, Box, Button, CircularProgress, Paper, Stack, Typography } from '@mui/material';
import { useAuth } from 'hooks/useAuth';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  CriarListaPayload,
  criarLista as criarListaService,
  listarMinhasListas,
  trocarTemplateLista as trocarTemplateListaService,
} from 'services/adminListas';
import { trackCreateListSuccess } from 'utils/analytics';
import { BUILD_PROFILE } from 'utils/buildProfile';
import {
  setGoogleAdsUserData,
  trackGoogleAdsConversion,
  trackGoogleTagEvent,
} from 'utils/googleTag';
import { trackEvent } from 'utils/tracking';

const DEVICE_ID_STORAGE_KEY = 'quick_create_device_id_v1';

const TEMPLATE_OPTIONS = [
  { slug: 'cha-de-bebe', label: 'Chá de bebê' },
  { slug: 'aniversario', label: 'Aniversário' },
  { slug: 'casamento', label: 'Casamento' },
  { slug: 'vaquinha', label: 'Vaquinha' },
] as const;

const SINGLE_OPTION_LABEL_BY_TEMPLATE: Record<string, string> = {
  'cha-de-bebe': 'Chá de bebê',
  casamento: 'Casamento',
};

const gerarSlugAleatorio = () =>
  Math.random()
    .toString(36)
    .replace(/[^a-z0-9]+/gi, '')
    .slice(-5) || 'lista';

const CriarListaPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, loading, loginAsGuest, user } = useAuth();
  const submitLockRef = useRef(false);
  const autoCreateStartedRef = useRef(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  const templateFromQuery = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('template') || '';
  }, [location.search]);

  const allowedTemplates = useMemo(() => {
    if (BUILD_PROFILE.forceTemplateSlug) {
      return [
        {
          slug: BUILD_PROFILE.forceTemplateSlug,
          label: SINGLE_OPTION_LABEL_BY_TEMPLATE[BUILD_PROFILE.forceTemplateSlug] || 'Modelo',
        },
      ];
    }

    return TEMPLATE_OPTIONS;
  }, []);

  const selectedTemplateSlug = useMemo(() => {
    if (BUILD_PROFILE.forceTemplateSlug) {
      return BUILD_PROFILE.forceTemplateSlug;
    }
    const optionExists = allowedTemplates.some((item) => item.slug === templateFromQuery);
    return optionExists ? templateFromQuery : '';
  }, [allowedTemplates, templateFromQuery]);

  const obterDeviceId = () => {
    const existente = localStorage.getItem(DEVICE_ID_STORAGE_KEY);
    if (existente) return existente;

    const novo = (
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
    ).replace(/[^a-zA-Z0-9_-]/g, '');

    localStorage.setItem(DEVICE_ID_STORAGE_KEY, novo);
    return novo;
  };

  const executarCriacao = useCallback(
    async (templateSlug: string) => {
      if (submitLockRef.current) {
        trackEvent('create_list_blocked_inflight', {
          source: 'template_picker',
        });
        return;
      }

      submitLockRef.current = true;

      if (loading) {
        trackEvent('create_list_blocked_session_loading', {
          source: 'template_picker',
        });
        setInfo('Validando sua sessão, tente novamente em alguns segundos.');
        submitLockRef.current = false;
        return;
      }

      setCreating(true);
      setError('');
      setInfo('');

      const templateSlugEfetivo =
        templateSlug || BUILD_PROFILE.forceTemplateSlug || 'personalizado';

      const payload: CriarListaPayload = {
        tituloLista: 'Minha lista',
        publica: false,
        slug: gerarSlugAleatorio(),
        templateSlug: templateSlugEfetivo || undefined,
        tipoLista: templateSlugEfetivo,
      };

      try {
        if (!isAuthenticated) {
          const deviceId = obterDeviceId();
          await loginAsGuest(deviceId);
        }

        const data = await criarListaService(payload);
        const listaCriada = data?.lista;

        if (!listaCriada?.id) {
          throw new Error('Não foi possível identificar a lista criada.');
        }

        trackEvent('create_list_success', {
          source: 'template_picker',
          listId: listaCriada.id,
          templateSlug: templateSlugEfetivo,
        });
        trackCreateListSuccess({
          source: 'template_picker',
          listId: listaCriada.id,
          templateSlug: templateSlugEfetivo,
          guestFlow: !isAuthenticated,
          brand: BUILD_PROFILE.brand,
        });
        trackGoogleTagEvent('create_list', {
          event_category: 'engagement',
          event_label: BUILD_PROFILE.brand,
          list_id: listaCriada.id,
          template_slug: templateSlugEfetivo,
          guest_flow: !isAuthenticated,
        });
        setGoogleAdsUserData({
          email: user?.email,
          phoneNumber: user?.telefone,
          fullName: user?.nome,
        });
        trackGoogleAdsConversion(
          BUILD_PROFILE.googleAdsId,
          BUILD_PROFILE.googleAdsConversionLabel,
          listaCriada.id
        );
        const slugCriado = String(listaCriada.slug || '').trim();
        if (slugCriado) {
          navigate(`/${slugCriado}`, { replace: true });
        } else {
          navigate('/admin/listas', {
            replace: true,
            state: { mensagemSucesso: 'Lista criada com sucesso.' },
          });
        }
      } catch (err: any) {
        const rawMessage = String(err?.response?.data?.error || '').trim();

        // Usuário já tem uma lista — redirecionar direto para Minhas listas
        const normalizedForCheck = rawMessage
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .toLowerCase();
        if (normalizedForCheck.includes('você ja tem uma lista')) {
          // Já tem lista: aplica o tipo escolhido na lista existente em vez de ignorar
          try {
            const listas = await listarMinhasListas();
            const listaExistente = listas.find((item) => item.slug);
            if (listaExistente) {
              const templateAtual = listaExistente.template?.slug ?? listaExistente.tipoLista;
              if (templateAtual !== templateSlugEfetivo) {
                try {
                  await trocarTemplateListaService(listaExistente.id, templateSlugEfetivo);
                  trackEvent('create_list_swapped_template', {
                    source: 'template_picker',
                    listId: listaExistente.id,
                    templateSlug: templateSlugEfetivo,
                  });
                } catch {
                  // se a troca falhar, ainda leva para a lista existente
                }
              }
              navigate(`/${listaExistente.slug}`, { replace: true });
            } else {
              navigate('/admin/listas', { replace: true });
            }
          } catch {
            navigate('/admin/listas', { replace: true });
          }
          return;
        }

        const normalizedMessage = rawMessage
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .toLowerCase();
        const message = normalizedMessage.includes('dados invalidos')
          ? 'Não foi possível criar a lista com esses dados.'
          : rawMessage || 'Não foi possível criar a lista agora.';
        trackEvent('create_list_error', {
          source: 'template_picker',
          templateSlug: templateSlugEfetivo,
          status: err?.response?.status,
          error: message,
        });
        setError(message);
      } finally {
        setCreating(false);
        submitLockRef.current = false;
      }
    },
    [isAuthenticated, loading, loginAsGuest, navigate, user?.email, user?.nome, user?.telefone]
  );

  useEffect(() => {
    if (autoCreateStartedRef.current) return;
    if (!selectedTemplateSlug) return;
    if (loading) return; // aguarda auth resolver antes de tentar criar

    autoCreateStartedRef.current = true;
    void executarCriacao(selectedTemplateSlug);
  }, [executarCriacao, selectedTemplateSlug, loading]);

  return (
    <Box maxWidth={820} mx="auto">
      <Stack spacing={2} mb={3} textAlign="center">
        <Typography variant="h4">Escolha o modelo da lista</Typography>
        <Typography variant="body1" color="text.secondary">
          Selecione uma opção abaixo para criar sua lista automaticamente.
        </Typography>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {info && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {info}
        </Alert>
      )}

      <Paper elevation={3} sx={{ p: { xs: 2, sm: 3 } }}>
        <Stack direction="row" flexWrap="wrap" useFlexGap gap={1.5} justifyContent="center">
          {allowedTemplates.map((template) => (
            <Button
              key={template.slug}
              variant="contained"
              size="large"
              onClick={() => void executarCriacao(template.slug)}
              disabled={creating || loading}
              sx={{ minWidth: { xs: '100%', sm: 180 }, flex: '1 1 180px' }}
            >
              {creating && selectedTemplateSlug === template.slug ? (
                <Stack direction="row" spacing={1} alignItems="center">
                  <CircularProgress size={18} color="inherit" />
                  <span>Criando...</span>
                </Stack>
              ) : (
                template.label
              )}
            </Button>
          ))}
        </Stack>
      </Paper>
    </Box>
  );
};

export default CriarListaPage;
